import {
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { FormEvent, useRef, useState } from "react";
import { Button } from "../Button";
import { useModal } from "../../hooks/useModal";
import { colors } from "../../colors";
import { useShowToast } from "../../hooks/useShowToast";
import { useAuth } from "../../hooks/useAuth";
import { styled } from "styled-components";
import icon from "../../assets/skipgive-icon.png";
import { Image } from "../Reusable";

const SuccessWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 2rem;
`;

export const ForgotPasswordModal = () => {
  const { resetPassword } = useAuth();
  const { closeModal, activeModal } = useModal();
  const { showToast } = useShowToast();
  const [email, setEmail] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const initialRef = useRef(null);
  const finalRef = useRef(null);

  if (activeModal !== "forgot-password") return null;

  const handleResetPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsProcessing(true);

    if (!email) {
      showToast("Please enter your email address!", "warning");
      setIsProcessing(false);
      return;
    }

    const { error } = await resetPassword(email);

    if (error) {
      showToast("There was an error sending the reset email!", "error");
      setIsProcessing(false);
      return;
    }

    setEmailSent(true);
    setIsProcessing(false);
  };

  const form = (
    <form onSubmit={handleResetPassword}>
      <ModalHeader fontSize={"2xl"}>Reset Password</ModalHeader>
      <ModalCloseButton />
      <ModalBody pb={6}>
        <Text mb={4} color="#4a5568">
          Enter your email address and we'll send you a link to reset your
          password.
        </Text>
        <FormControl>
          <FormLabel>Email</FormLabel>
          <Input
            ref={initialRef}
            placeholder="mary@example.com"
            focusBorderColor={colors.gold + "60"}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
          />
        </FormControl>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={closeModal}
          buttonType="secondary"
          label="Cancel"
          type="button"
        />
        <Button
          buttonType="primary"
          label="Send Reset Link"
          isLoading={isProcessing}
          type="submit"
        />
      </ModalFooter>
    </form>
  );

  const successMessage = (
    <SuccessWrapper>
      <Image src={icon} />
      <ModalHeader margin={"-3"} fontSize={"3xl"}>
        Check Your Email
      </ModalHeader>
      <Text textAlign="center" paddingX="2rem" color="#4a5568">
        We've sent a password reset link to <strong>{email}</strong>. Please
        check your inbox and follow the instructions to reset your password.
      </Text>
      <div style={{ marginTop: "2rem" }}>
        <Button
          onClick={() => {
            setEmailSent(false);
            closeModal();
          }}
          buttonType="primary"
          label="Got It"
          type="button"
        />
      </div>
    </SuccessWrapper>
  );

  return (
    <Modal
      initialFocusRef={initialRef}
      finalFocusRef={finalRef}
      isOpen={true}
      onClose={() => {
        setEmailSent(false);
        closeModal();
      }}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent margin="auto">
        {emailSent ? successMessage : form}
      </ModalContent>
    </Modal>
  );
};
